export type SceneState = 'menu' | 'playing' | 'gameOver';

export interface SceneManager<S extends string = SceneState> {
  readonly state: S;
  transition(next: S): void;
  on(state: S, handler: () => void): void;
  onExit(state: S, handler: () => void): void;
}

export function createSceneManager(initial: SceneState): SceneManager<SceneState>;
export function createSceneManager<S extends string>(initial: S): SceneManager<S>;
export function createSceneManager<S extends string>(
  initial: S,
): SceneManager<S> {
  let current: S = initial;
  const enter = new Map<S, Array<() => void>>();
  const exit = new Map<S, Array<() => void>>();

  const push = (map: Map<S, Array<() => void>>, key: S, fn: () => void): void => {
    const list = map.get(key);
    if (list) list.push(fn);
    else map.set(key, [fn]);
  };

  return {
    get state() {
      return current;
    },
    transition(next: S): void {
      const exitHandlers = exit.get(current);
      if (exitHandlers) for (const h of exitHandlers) h();
      current = next;
      const enterHandlers = enter.get(next);
      if (enterHandlers) for (const h of enterHandlers) h();
    },
    on(state, handler) {
      push(enter, state, handler);
    },
    onExit(state, handler) {
      push(exit, state, handler);
    },
  };
}
